import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { BookOpen, Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("details"); // details | otp
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setStep("otp");
      if (data.dev_otp) toast.info(`Dev code: ${data.dev_otp}`);
      else toast.success("We emailed you a 6-digit code");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      if (data.dev_otp) toast.info(`Dev code: ${data.dev_otp}`);
      else toast.success("New code sent");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12 bg-slate-50">
      <div className="w-full max-w-md bg-white rounded-2xl border border-border shadow-sm p-8 animate-fade-up">
        {step === "details" ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg tracking-tight">Create account</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">Buy notes and sell your own — one account.</p>
            <form onSubmit={requestOtp} className="space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" data-testid="register-name" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="register-email" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" data-testid="register-password" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="register-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send verification code"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground text-center mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link>
            </p>
          </>
        ) : (
          <>
            <button onClick={() => setStep("details")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="otp-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MailCheck className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900">Verify your email</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">
              Enter the 6-digit code we sent to <span className="font-medium text-slate-700">{email}</span>
            </p>
            <form onSubmit={verifyOtp} className="space-y-5">
              <div className="flex justify-center" data-testid="otp-input">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {error && <p className="text-sm text-destructive text-center" data-testid="otp-error">{error}</p>}
              <Button type="submit" className="w-full rounded-full" disabled={loading || otp.length !== 6} data-testid="otp-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & create account"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground text-center mt-6">
              Didn't get it?{" "}
              <button onClick={resend} className="text-primary font-medium hover:underline" data-testid="otp-resend">Resend code</button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
